import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'
import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { Button } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { PageSection } from '@ui/components/PageSection'
import { CirclePlusIcon } from '@ui/icons/CirclePlus.icon'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { TableNoResults } from '@ui/v2/Table/Table'
import { FilterOptions, TableFilter } from '@ui/v2/TableFilter/TableFilter'

import { PageQuery, useApiClient } from '@utils/ApiClient'
import { conditionalReverse } from '@utils/array'
import { ClientApiClient } from '@utils/clientApi'
import { formatDate } from '@utils/date.utils'
import { useDebouncedValue } from '@utils/debounce'
import { useWindowResize } from '@utils/domUtils'
import { getAppliedFiltersLength, isAnyFilterActive } from '@utils/filter.utils'
import { getCurrencySymbol } from '@utils/transaction.utils'

import { FilterQueryProps } from '@model/FilterFields'
import { SubscriptionHistoryItem, getSubscriptionRequestTypeLabel } from '@model/SubscriptionsDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useGenerateDocumentFile } from '@hooks/useGenerateDocumentFile'
import { ScrollToIds } from '@hooks/useScrollToElementIds'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { SubscriptionBadgeMap } from '@shared/components/StatusBadge/StatusBadge'

import { SubscriptionsHistoryCard } from './SubscriptionsHistoryCard'
import { SubscriptionsHistoryTable } from './SubscriptionsHistoryTable'

const initialQuery = {
  ca_search_TextSearch: undefined,
}

interface OuterProps {
  data?: SubscriptionHistoryItem[]
}

export interface ISubscriptionsHistoryQuery extends FilterQueryProps {
  DateFrom?: string
  DateTo?: string
}

interface useSubscriptionsHistoryFetchProps {
  search: ReturnType<typeof useSearch>
}

const useSubscriptionsHistoryFetch = (props: useSubscriptionsHistoryFetchProps) => {
  const { search } = props

  const apiClient = useApiClient(ClientApiClient)
  const initialRender = useRef(true)

  const searchKey = {
    ...search.search,
    ca_search_TextSearch: search.debouncedSearchTerm,
    pageQuery: search.pageQuery,
  }

  const result = usePaginatedQuery({
    queryKey: queryKeys.subscriptionHistory(searchKey),
    queryFn: (pageQuery) =>
      apiClient.getSubscriptionsHistory({
        ...pageQuery,
        ...search.pageQuery,
        search: { ...search.search, ca_search_TextSearch: search.searchTerm },
        caller: 'ca',
      }),
  })

  useEffect(
    () => {
      if (initialRender.current) {
        initialRender.current = false
      } else {
        const timer = setTimeout(
          () =>
            search.searchTerm !== undefined &&
            result.setPageQuery!({
              ...result.pageQuery,
              pageIndex: 1,
              ca_search_TextSearch: search.searchTerm,
            }),
          500
        )
        return () => clearTimeout(timer)
      }
    },
    [search.searchTerm] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return {
    ...result,
    subscriptions: result.data,
  }
}

const useSubscriptionsHistoryDocumentFile = (data: SubscriptionHistoryItem[]) => {
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const { formatNumber } = useFormatNumber()
  const isArabic = useArabicSessionLanguage()

  const tableBody = () => {
    const headerCsvData = [
      [
        t('Date'),
        t('Subscriptions.Product'),
        t('Subscriptions.Request Type'),
        t('Subscriptions.Price'),
        t('Subscriptions.Status'),
      ],
    ]

    return data.reduce((previousValue, currentValue) => {
      return previousValue.concat([
        conditionalReverse(
          [
            formatDate(currentValue.createdDate, { formatType: dateFormat }),
            currentValue.subscription.name,
            getSubscriptionRequestTypeLabel(currentValue.type.id, t),
            `${getCurrencySymbol('USD')} ${formatNumber(currentValue.amount)}`,
            t(SubscriptionBadgeMap[currentValue.state.id].label),
          ],
          isArabic
        ),
      ])
    }, headerCsvData)
  }

  return useGenerateDocumentFile('ib_contest-campaign', tableBody())
}

const useSearch = () => {
  const [search, setSearch] = useState<ISubscriptionsHistoryQuery>(initialQuery)
  const [searchTerm, setSearchTerm] = useState<string | null>(null)
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500)
  const [pageQuery, setPageQuery] = useState<PageQuery>()

  const handleFilterSearch = (q: FilterQueryProps): void => {
    setSearch(q)
  }

  const handleClearSearch = () => {
    setSearchTerm('')
    setSearch(initialQuery)
  }

  return {
    handleFilterSearch: handleFilterSearch,
    clearSearch: handleClearSearch,
    filtersLength: getAppliedFiltersLength({ ...search }),
    search,
    setSearch,
    searchTerm,
    debouncedSearchTerm,
    setSearchTerm,
    pageQuery,
    setPageQuery,
  }
}

export const SubscriptionsHistoryPage: React.FC<OuterProps> = () => {
  const { t } = useTranslation()
  useScrollToTop()

  const sortOptions = [
    { id: 'CreatedDate', name: t('Date') },
    {
      id: 'ClientSubscriptionDetail.SubscriptionDetail.Subscription.Name',
      name: t('Subscriptions.Product'),
    },
    {
      id: 'SubscriptionRequestTypeId',
      name: t('Subscriptions.Request Type'),
    },
    {
      id: 'ClientSubscriptionDetail.SubscriptionDetail.Amount',
      name: t('Subscriptions.Price'),
    },
    {
      id: 'state.Order',
      name: t('Subscriptions.Status'),
    },
  ]

  const isMobile = useWindowResize()
  const search = useSearch()

  const query = useSubscriptionsHistoryFetch({ search })
  const generateDocumentFile = useSubscriptionsHistoryDocumentFile(query.data)
  const { pageQuery, setPageQuery, meta, isLoading } = query

  const [isFilterVisible, setIsFilterVisible] = useState(false)

  const handleSearchFilters = React.useCallback(
    (searchFilters?: FilterQueryProps) => {
      if (!searchFilters) return
      search.handleFilterSearch(searchFilters)
      search.setPageQuery?.({ ...search.pageQuery, pageIndex: 1 })
    },
    [search]
  )

  const noResults = () => {
    if (query.data.length !== 0) {
      return false
    }

    if (isAnyFilterActive(search.search, search.searchTerm)) {
      return true
    }

    return (
      <TableNoResults title={t('Subscriptions.You have no Subscriptions yet')}>
        <Link to={'/dashboard/subscriptions/trading-platforms'}>
          <Button.Primary
            iconLeft={{ icon: CirclePlusIcon, color: 'text-white' }}
            type='button'
            size='md'
          >
            {t('Subscriptions.Add Subscription')}
          </Button.Primary>
        </Link>
      </TableNoResults>
    )
  }

  return (
    <React.Fragment>
      <PageSection
        id={ScrollToIds.SubscriptionHistoryETDHeader}
        title={t('Subscriptions.Subscription History')}
        searchField={{
          label: t('Search Product Name'),
          value: search.searchTerm,
          onChange: (v: string) => search.setSearchTerm(v),
          canClear: true,
        }}
        sortOptions={sortOptions}
        pageQuery={pageQuery}
        setPageQuery={setPageQuery}
        onFilterClick={() => setIsFilterVisible((s) => !s)}
        documentFile={generateDocumentFile}
      />

      <TableFilter
        isVisible={isFilterVisible}
        searchFilters={search.search}
        filterOptions={[
          FilterOptions.Date,
          FilterOptions.Price,
          FilterOptions.SubscriptionType,
          FilterOptions.SubscriptionLevel,
          FilterOptions.SubscriptionClassificationType,
          FilterOptions.SubscriptionStatus,
          FilterOptions.SubscriptionRequestType,
        ]}
        setSearchFilters={handleSearchFilters}
      />

      {isMobile ? (
        <SubscriptionsHistoryCard isLoading={isLoading} noResults={noResults()} data={query.data} />
      ) : (
        <SubscriptionsHistoryTable
          data={query.data}
          isLoading={isLoading}
          search={search}
          noResults={noResults()}
          sortOptions={sortOptions}
        />
      )}
      {meta && meta.itemsCount > 0 && (
        <Pagination
          scrollToHeaderId={ScrollToIds.SubscriptionHistoryETDHeader}
          pageData={meta}
          isLoading={isLoading}
          onPageChanged={(pageIndex, pageSize) => {
            setPageQuery!({
              ...pageQuery,
              pageIndex,
              pageSize,
            })
          }}
        />
      )}
    </React.Fragment>
  )
}
